import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { injectIntl, FormattedMessage, IntlShape } from 'react-intl'
import validator from 'validator'
import userService from 'services/user'
import ActionsModal from 'components/ActionsModal'
import { selectDisplayedUser, updateDisplayedUser, closeDisplayedUser } from 'store/organizations'
import { selectCurrentUser, selectOrganizations, selectUsers, selectAllGroups, fetchGroups } from 'store/global'
import apiService from 'services/api'
import messages from './messages'
import DropdownInput from 'components/DropdownInput'
import { Select } from 'antd'
import styles from './UserModal.scss'

const allowedModes = ['create', 'edit', 'view'] as const
const [MODE_CREATE, MODE_EDIT, MODE_VIEW] = allowedModes

type Mode = typeof allowedModes[number]

interface UserModalProps {
  intl: IntlShape
  mode?: Mode
  onSubmit: (userData: any) => void
}

function UserModal({ intl, mode = MODE_CREATE, onSubmit }: UserModalProps) {
  const dispatch = useDispatch()

  const allUsers = useSelector(selectUsers)
  const groupsList = useSelector(selectAllGroups)
  const currentUser = useSelector(selectCurrentUser)
  const displayedUser = useSelector(selectDisplayedUser)
  const organizations = useSelector(selectOrganizations)

  const isSuperuser = userService.isSuperuser(currentUser)
  const isAdmin = userService.isAdmin(currentUser)

  const [userGroups, setUserGroups] = useState<any[]>([])
  const [isLoadingUserGroups, setIsLoadingUserGroups] = useState(false)
  const [keycloakOrganizations, setKeycloakOrganizations] = useState<any[]>([])
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false)
  const roles = [
    { value: 'admin', label: 'Admin' },
    { value: 'super-user', label: 'Super User' },
    { value: 'basic-user', label: 'Basic User' },
  ]
  const flattenGroupsForOptions = (groups: any[], level = 0): any[] => {
    let allGroups: any[] = []
    groups.forEach(group => {
      allGroups.push({
        value: group.id,
        label: '  '.repeat(level) + group.path,
        path: group.path,
      })
      if (group.subGroups && group.subGroups.length > 0) {
        allGroups = allGroups.concat(flattenGroupsForOptions(group.subGroups, level + 1))
      }
    })
    return allGroups
  }

  const groupOptions = flattenGroupsForOptions(groupsList || [])

  const groupSelectedOptions = useMemo(() => {
    if (!displayedUser || !groupsList) {
      return []
    }
    let userGroupIds: string[] = []
    if (mode === MODE_EDIT) {
      userGroupIds = displayedUser.groupIds || []
    } else if (mode === MODE_VIEW) {
      userGroupIds = userGroups.length > 0 ? userGroups.map(group => group.id) : []
    } else {
      userGroupIds = displayedUser.groupIds || []
    }

    const flattenGroupsForLookup = (groups: any[]): any[] => {
      let allGroups: any[] = []
      groups.forEach(group => {
        allGroups.push(group)
        if (group.subGroups && group.subGroups.length > 0) {
          allGroups = allGroups.concat(flattenGroupsForLookup(group.subGroups))
        }
      })
      return allGroups
    }

    const allGroupsFlat = flattenGroupsForLookup(groupsList || [])

    return userGroupIds
      .map(groupId => {
        const group = allGroupsFlat.find(group => group.id === groupId)
        return group ? { value: groupId, label: group.path } : null
      })
      .filter(option => option !== null)
  }, [displayedUser, groupsList, userGroups, mode])

  useEffect(() => {
    if (!groupsList || groupsList.length === 0) {
      dispatch(fetchGroups())
    }
  }, [dispatch, groupsList])

  useEffect(() => {
    const fetchKeycloakOrganizations = async () => {
      setIsLoadingOrganizations(true)
      try {
        const response = await apiService.getOrganizations()
        setKeycloakOrganizations(response || [])
      } catch (error) {
        setKeycloakOrganizations([])
      } finally {
        setIsLoadingOrganizations(false)
      }
    }

    fetchKeycloakOrganizations()
  }, [mode])

  useEffect(() => {
    const fetchUserGroups = async () => {
      if ((mode === MODE_EDIT || mode === MODE_VIEW) && displayedUser?.id) {
        setIsLoadingUserGroups(true)
        try {
          const response = await apiService.getUserGroups(displayedUser.id)
          const groups = response.groups || []
          setUserGroups(groups)

          if (mode === MODE_EDIT && groups.length > 0) {
            const groupIds = groups.map((group: any) => group.id)
            dispatch(updateDisplayedUser({ key: 'groupIds', value: groupIds }))
          }
          try {
            const rolesRes = await apiService.getUserRoles(displayedUser.id)
            const clientRoles = rolesRes.roles || []
            const selectedRoleNames = clientRoles.map((r: any) => r.name)
            if (selectedRoleNames.length > 0 && mode !== MODE_VIEW) {
              dispatch(updateDisplayedUser({ key: 'roles', value: selectedRoleNames }))
              dispatch(updateDisplayedUser({ key: 'role', value: selectedRoleNames[0] }))
            }
          } catch (e) {}
        } catch (error) {
          setUserGroups([])
        } finally {
          setIsLoadingUserGroups(false)
        }
      }
    }

    fetchUserGroups()
  }, [mode, displayedUser?.id, dispatch])

  useEffect(() => {
    if (!organizations || organizations.length === 0) {
      return
    }

    const [firstOrganization] = organizations
    const defaultClientRole = 'basic-user'

    if (!firstOrganization) {
      return
    }

    if (isAdmin && mode === MODE_CREATE) {
      if (!displayedUser?.role) {
        dispatch(updateDisplayedUser({ key: 'role', value: defaultClientRole }))
      }
      return
    }

    if ([MODE_VIEW, MODE_EDIT].includes(mode)) {
      if (isSuperuser) {
        const organizationId = displayedUser?.organization ?? displayedUser?.organizationId
        if (organizationId) {
          dispatch(updateDisplayedUser({ key: 'organization', value: organizationId }))
        }
      }

      const nextRole = displayedUser?.role || defaultClientRole
      dispatch(updateDisplayedUser({ key: 'role', value: nextRole }))

      if (userGroups.length > 0 && mode === MODE_VIEW) {
        const groupIds = userGroups.map(group => group.id)
        dispatch(updateDisplayedUser({ key: 'groupIds', value: groupIds }))
      }
    }
  }, [organizations, isSuperuser, isAdmin, mode, displayedUser, userGroups, dispatch])

  const onCloseModal = () => dispatch(closeDisplayedUser())

  const onFieldChange = (key: string, value: any) => dispatch(updateDisplayedUser({ key, value }))

  const onFormSubmit = async (displayedUser: any) => {
    const userData = {
      ...displayedUser,
      groupIds: groupSelectedOptions.map(option => option.value),
    }
    if (mode === MODE_CREATE) {
      if (Array.isArray(userData.roles)) delete userData.roles
      if (!userData.role && displayedUser?.chosenRole) {
        userData.role = displayedUser.chosenRole
      }
      if (userData.role) {
        userData.chosenRole = userData.role
      }
    }
    onSubmit(userData)
  }

  const onGroupsChange = (key: string, nextGroupsSelections: { value: string }[]) => {
    const nextGroupIds = nextGroupsSelections.map(({ value }) => value)
    onFieldChange(key, nextGroupIds)

    dispatch(updateDisplayedUser({ key: 'groupIds', value: nextGroupIds }))
  }

  const isEmailValidAndAvailable = (email: string) => {
    if (!email || typeof email !== 'string') {
      return false
    }
    return validator.isEmail(email) && allUsers.every((user: any) => user.email !== email)
  }

  // Validation for required fields in create mode
  const isFormValid = useMemo(() => {
    if (mode !== MODE_CREATE) return true
    
    const hasEmail = displayedUser?.email && validator.isEmail(displayedUser.email)
    const hasFirstName = displayedUser?.firstName && displayedUser.firstName.trim().length > 0
    const hasLastName = displayedUser?.lastName && displayedUser.lastName.trim().length > 0
    const hasOrganization = displayedUser?.organization
    
    return hasEmail && hasFirstName && hasLastName && hasOrganization
  }, [mode, displayedUser?.email, displayedUser?.firstName, displayedUser?.lastName, displayedUser?.organization])

  const fields = {
    organization: {
      key: 'organization',
      type: 'select',
      label: <FormattedMessage {...messages.labels.organization} />,
      options: mode === MODE_CREATE ? keycloakOrganizations : (organizations || []),
      isRequired: true,
      disabled: mode === MODE_EDIT,
      renderInput: () => {
        if (isLoadingOrganizations) {
          return <div>Loading organizations...</div>
        }

        if (mode === MODE_EDIT) {
          const userOrganization =
            keycloakOrganizations.find(org => org.id === displayedUser.organization)?.name ||
            organizations?.find((org: any) => org.id === displayedUser.organization)?.name ||
            displayedUser.organization ||
            'No organization assigned'
          return <div className={styles.readOnlyField}>{userOrganization}</div>
        }

        const orgOptions = keycloakOrganizations.map(org => ({
          value: org.id,
          label: org.name,
        }))

        return (
          <Select
            placeholder='Select organization'
            value={displayedUser.organization}
            onChange={(value: string) => onFieldChange('organization', value)}
            options={orgOptions}
            style={{ width: '100%' }}
            showSearch
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          />
        )
      },
    },
    email: {
      key: 'email',
      label: <FormattedMessage {...messages.labels.email} />,
      isRequired: true,
      disabled: mode !== MODE_CREATE,
      validator: isEmailValidAndAvailable,
      formFeedback: (
        <FormattedMessage
          {...messages.formFeedback[validator.isEmail(displayedUser.email || '') ? 'emailUnavailable' : 'invalidEmail']}
        />
      ),
    },
    firstName: {
      key: 'firstName',
      label: <FormattedMessage {...messages.labels.firstName} />,
      isRequired: true,
      validator: (value: string) => value.length > 0,
    },
    lastName: {
      key: 'lastName',
      label: <FormattedMessage {...messages.labels.lastName} />,
      isRequired: true,
      validator: (value: string) => value.length > 0,
    },
    roles: {
      key: 'roles',
      type: 'select',
      label: <FormattedMessage {...messages.labels.role} />,
      isRequired: false,
      disabled: mode === MODE_VIEW || mode === MODE_EDIT,
      renderInput: () => {
        if (mode === MODE_VIEW || mode === MODE_EDIT) {
          const currentRoles = displayedUser.roles || (displayedUser.role ? [displayedUser.role] : [])
          const labels = currentRoles.length ? currentRoles.join(', ') : 'No roles assigned'
          return <div className={styles.readOnlyField}>{labels}</div>
        }

        if (mode === MODE_CREATE) {
          return (
            <Select
              placeholder='Select role'
              value={displayedUser.role || displayedUser.chosenRole}
              onChange={(value: string) => {
                onFieldChange('role', value)
                onFieldChange('chosenRole', value)
              }}
              options={roles}
              style={{ width: '100%' }}
            />
          )
        }

        return null
      },
    },
    groups: {
      key: 'groupIds',
      isRequired: false,
      label: <FormattedMessage {...messages.labels.groups} />,
      disabled: mode === MODE_VIEW,
      renderInput: () => {
        if (isLoadingUserGroups) {
          return <div>Loading groups...</div>
        }

        if (mode === MODE_VIEW) {
          const groupPaths = userGroups.map(group => group.path)
          return (
            <div className={styles.readOnlyField}>
              {groupPaths.length > 0 ? groupPaths.join(', ') : 'No groups assigned'}
            </div>
          )
        }

        return (
          <div>
            <DropdownInput
              options={groupOptions}
              selections={groupSelectedOptions}
              onChange={(nextGroupsSelections: { value: string }[]) => {
                onGroupsChange('groupIds', nextGroupsSelections)
              }}
              hasSelectAll={false}
              disableSearch={false}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              Selected: {groupSelectedOptions.length} groups
            </div>
          </div>
        )
      },
    },
  }

  return (
    <ActionsModal
      type='user'
      mode={mode}
      item={displayedUser}
      primaryKey='email'
      fields={fields}
      onFieldChange={onFieldChange}
      onClose={onCloseModal}
      onSubmit={() => onFormSubmit(displayedUser)}
      customValidation={isFormValid}
    ></ActionsModal>
  )
}

export default injectIntl(UserModal)
